import Vue from 'vue';
import _ from 'lodash';
import moment from 'moment';
import { iwalyNotify } from '@/notify';
import {
  ordersSearch,
  orderDetails,
  joinOrder,
  fetchChatMessages,
  sendChatMessage,
  cancelOrder,
  orderAssign,
  orderClose, orderPayout,
} from '@/api/orders';
import {
  promocodesGet,
  promocodesCreatePromocode,
  promocodesDeactivatePromocode,
} from '@/api/promocodes';
import {
  SET_STATUSES,
  SET_ORDERS,
  SET_ORDER,
  UNSET_STATUSES,
  SET_JOINED_DISPUTE,
  SET_ORDER_MESSAGES,
  CLEAR_ORDER_MESSAGES,
  SET_DISPUTE_COUNT,
} from './mutation-types';

const getOrderAbbreviation = (order) => {
  const displayAbbreviation = _.get(order, 'displayCategory.abbreviation');
  if (displayAbbreviation) {
    return displayAbbreviation;
  }
  const firstService = _.head(_.get(order, 'services') || []);
  return _.get(order, 'firstServiceCategory.abbreviation')
    || _.get(firstService, 'service.category.abbreviation');
};

export default {
  state: {
    filterStatuses: {},
    data: {},
    disputeCount: 0,
    order: [],
    messenger: {},
  },
  getters: {
    order: (state) => (id) => {
      if (typeof id !== 'undefined') {
        return state.order.find((obj) => obj.id === parseInt(id, 10)) || null;
      }
      return state.order;
    },
    messenger: (state) => (id) => _.get(state.messenger, id) || {},
  },
  actions: {
    ACT_FETCH_ORDERS: async ({ commit, dispatch }, {
      key,
      statuses,
      offset,
      limit,
      orderBy,
      search,
    }) => {
      if (key !== 'promocodes') {
        dispatch('wait/start', `fetch.orders.${statuses.join(',')}`, { root: true });
        return ordersSearch({
          states: statuses,
          offset,
          limit,
          orderBy,
          search,
        }).then((response) => {
          const data = _.get(response, 'data.result') || [];
          const total = _.get(response, 'data.total') || null;
          commit(SET_ORDERS, {
            key,
            data: data.map((order) => ({
              ...order,
              name: `${getOrderAbbreviation(order)}-${order.id}`,
            })),
            total,
          });
          if (key === 'dispute') {
            commit(SET_DISPUTE_COUNT, total || 0);
          }
        }).finally(() => {
          dispatch('wait/end', `fetch.orders.${statuses.join(',')}`, { root: true });
        });
      }
      dispatch('wait/start', 'fetch.orders.promocodes', { root: true });
      return promocodesGet().then((response) => {
        commit(SET_ORDERS, {
          key,
          data: response.data,
        });
      }).finally(() => {
        dispatch('wait/end', 'fetch.orders.promocodes', { root: true });
      });
    },
    ACT_FETCH_DISPUTE_COUNT: async ({ commit }) => ordersSearch({
      states: ['dispute'],
      offset: 0,
      limit: 1,
      orderBy: [
        { column: 'id', descending: 1 },
      ],
    }).then((response) => {
      commit(SET_DISPUTE_COUNT, _.get(response, 'data.total') || 0);
    }),
    ACT_SET_STATUSES: ({ commit }, { key, status }) => {
      commit(SET_STATUSES, {
        key,
        status,
      });
    },
    ACT_UNSET_STATUSES: ({ commit }, { key }) => {
      commit(UNSET_STATUSES, key);
    },
    ACT_FETCH_ORDER: async ({ commit, dispatch }, id) => {
      dispatch('wait/start', `fetch.order.${id}`, { root: true });
      return orderDetails(id).then((response) => {
        const data = _.get(response, 'data');
        if (data) {
          const subject = _.get(response, 'data.subject.id');
          dispatch(
            'objects/ACT_FETCH_OBJECT',
            subject,
            { root: true },
          );
          commit(SET_ORDER, {
            ...data,
            name: `${getOrderAbbreviation(data)}-${data.id}`,
          });
        }
      }).finally(() => {
        dispatch('wait/end', `fetch.order.${id}`, { root: true });
      });
    },
    ACT_JOIN_ORDER: async ({ commit }, id) => joinOrder(id).then((response) => {
      if (response.status === 200) {
        commit(SET_JOINED_DISPUTE, id);
      }
    }),
    ACT_FETCH_MESSAGES: async ({
      commit,
      getters,
    }, {
        id,
        withWho,
        referenceId = null,
        withRef = true,
      }) => {
      if (withRef) {
        const messages = _.get(getters.messenger(id)[withWho], 'messages') || null;
        if (messages && messages.length) {
          const head = _.head(messages);
          // eslint-disable-next-line no-param-reassign
          referenceId = _.get(head, 'id') || null;
        }
      }
      const offset = -20;
      return fetchChatMessages(id, {
        offset,
        referenceId,
        with: withWho,
      }).then((response) => {
        if (response.data) {
          const { length } = (_.get(response.data, 'messages') || []);
          const completed = length < Math.abs(offset);
          const payload = {
            id,
            withWho,
            data: response.data,
            completed,
          };
          commit(SET_ORDER_MESSAGES, payload);
          return Promise.resolve(payload);
        }
        return Promise.reject();
      });
    },
    ACT_SEND_MESSAGE: async ({ commit, dispatch }, {
      id,
      lastMessageId,
      text,
      attaches,
      to,
      withWho,
    }) => {
      const fd = new FormData();
      if (attaches.length) {
        // eslint-disable-next-line array-callback-return
        attaches.map(({ file }, index) => {
          fd.append(index, file);
        });
      }
      return new Promise((resolve) => {
        if (!attaches.length) {
          resolve([]);
        } else {
          dispatch(
            'attachments/ACT_UPLOAD_ATTACHMENT',
            fd,
            { root: true },
          ).then((uploaded) => resolve(uploaded));
        }
      }).then((uploaded) => sendChatMessage(id, {
        lastMessageId,
        message: {
          text,
          attaches: uploaded,
        },
        to,
      }).then((response) => {
        if (response.data) {
          commit(SET_ORDER_MESSAGES, {
            id,
            withWho,
            data: response.data,
          });
        }
      }));
    },
    // eslint-disable-next-line arrow-body-style
    ACT_CANCEL_ORDER: async ({ commit, getters }, { id, payload }) => {
      const order = getters.order(id);
      const joined = _.get(order, 'joined');
      return new Promise((resolve) => {
        if (joined) {
          return resolve();
        }
        return joinOrder(id).then(() => resolve());
      }).then(() => { // eslint-disable-line arrow-body-style
        return cancelOrder(id, payload).then((response) => {
          if (response.status === 200) {
            const data = _.get(response, 'data.order');
            commit(SET_ORDER, {
              ...data,
              name: `${getOrderAbbreviation(data)}-${data.id}`,
            });
            return Promise.resolve();
          }
          return Promise.reject();
        });
      });
    },
    ACT_CLEAR_MESSAGES: async ({ commit }, id) => {
      commit(CLEAR_ORDER_MESSAGES, id);
    },
    ACT_ORDER_ASSIGN: async ({
      commit,
      dispatch,
      getters,
      rootGetters,
    }, { id, contractor }) => {
      if (rootGetters['wait/is'](`assign.order.${id}`)) {
        return Promise.reject();
      }
      dispatch('wait/start', `assign.order.${id}`, { root: true });
      const order = getters.order(id);
      const joined = _.get(order, 'joined');
      return new Promise((resolve) => {
        if (joined) {
          return resolve();
        }
        return dispatch('ACT_JOIN_ORDER', id).then(() => resolve());
      }).then(() => { // eslint-disable-line arrow-body-style
        return orderAssign(id, {
          contractor,
        }).then((response) => {
          if (response.status === 200) {
            const data = _.get(response, 'data.order');
            commit(SET_ORDER, {
              ...data,
              name: `${getOrderAbbreviation(data)}-${data.id}`,
            });
            return Promise.resolve();
          }
          return Promise.reject();
        }).catch(() => Promise.reject()).finally(() => {
          dispatch('wait/end', `assign.order.${id}`, { root: true });
        });
      });
    },
    ACT_ORDER_CLOSE: async ({
      commit,
      dispatch,
      getters,
      rootGetters,
    }, { id, payload }) => {
      if (rootGetters['wait/is'](`close.order.${id}`)) {
        return Promise.reject();
      }
      dispatch('wait/start', `close.order.${id}`, { root: true });
      const order = getters.order(id);
      const joined = _.get(order, 'joined');
      return new Promise((resolve) => {
        if (joined) {
          return resolve();
        }
        return dispatch('ACT_JOIN_ORDER', id).then(() => resolve());
      }).then(() => { // eslint-disable-line arrow-body-style
        // eslint-disable-next-line no-param-reassign
        if (!payload) payload = {};
        return orderClose(id, {
          rating: null,
          ...payload,
        }).then((response) => {
          if (response.status === 200) {
            const data = _.get(response, 'data.order');
            commit(SET_ORDER, {
              ...data,
              name: `${getOrderAbbreviation(data)}-${data.id}`,
            });
            iwalyNotify.success({
              text: 'Спор успешно закрыт!',
            });
            return Promise.resolve();
          }
          return Promise.reject();
        }).catch(() => Promise.reject()).finally(() => {
          dispatch('wait/end', `close.order.${id}`, { root: true });
        });
      });
    },
    ACT_CREATE_PROMOCODE: async ({
      dispatch,
    }, payload) => new Promise((resolve, reject) => {
      dispatch('wait/start', 'create.promocode', { root: true });
      promocodesCreatePromocode(payload).then((response) => {
        if (response.status === 200) {
          iwalyNotify.success({
            text: 'Промокод успешно создан!',
          });
          resolve(response);
        }
      }, (error) => {
        console.log(error);
        reject(error);
      }).finally(() => {
        dispatch('wait/end', 'create.promocode', { root: true });
      });
    }),
    ACT_DEACTIVATE_PROMOCODE: async ({
      dispatch,
    }, payload) => new Promise((resolve, reject) => {
      dispatch('wait/start', 'deactivate.promocode', { root: true });
      promocodesDeactivatePromocode(payload).then((response) => {
        if (response.status === 200) {
          iwalyNotify.success({
            text: 'Промокод успешно деактивирован!',
          });
          resolve(response);
        }
      }, (error) => {
        console.log(error);
        reject(error);
      }).finally(() => {
        dispatch('wait/end', 'deactivate.promocode', { root: true });
      });
    }),
    ACT_ORDER_PAYOUT: async ({ dispatch }, id) => {
      try {
        dispatch('wait/start', `payout.order.${id}`, { root: true });
        await orderPayout(id);
      } finally {
        dispatch('wait/end', `payout.order.${id}`, { root: true });
      }
    },
  },
  mutations: {
    [SET_STATUSES](state, { key, status }) {
      const statuses = _.get(state.filterStatuses, key);
      Vue.set(state.filterStatuses, key, _.xor(statuses, [status]));
    },
    [SET_ORDERS](state, { key, data, total }) {
      Vue.set(state.data, key, {
        data,
        total,
      });
    },
    [SET_DISPUTE_COUNT](state, total) {
      state.disputeCount = total;
    },
    [SET_ORDER](state, data) {
      const foundIndex = _.findIndex(state.order, (order) => order.id === data.id);
      if (foundIndex > -1) {
        Vue.set(state.order, foundIndex, data);
      } else {
        state.order.push(data);
      }
    },
    [UNSET_STATUSES](state, key) {
      Vue.delete(state.filterStatuses, key);
    },
    [SET_JOINED_DISPUTE](state, id) {
      state.order = state.order.map((order) => {
        const payload = order;
        if (parseInt(order.id, 10) === parseInt(id, 10)) {
          payload.joined = true;
        }
        return payload;
      });
    },
    [SET_ORDER_MESSAGES](state, {
      id,
      withWho,
      data,
      completed,
    }) {
      const obj = _.get(state.messenger, id);
      if (!obj) {
        Vue.set(state.messenger, id, {});
      }
      const payload = _.get(state.messenger[id], withWho);
      if (!payload) {
        Vue.set(state.messenger[id], withWho, {
          ...data,
          messages: _.orderBy(data.messages, (message) => moment(message.createdAt).unix(), ['asc']),
        });
      } else {
        const set = {
          ...state.messenger[id][withWho],
          members: _.unionBy(payload.members, data.members, 'id'),
          messages: _(payload.messages)
            .unionBy(data.messages, 'id')
            .orderBy((message) => moment(message.createdAt).unix(), ['asc'])
            .value(),
        };
        if (typeof completed !== 'undefined') {
          set.completed = completed;
        }
        Vue.set(state.messenger[id], withWho, set);
      }
    },
    [CLEAR_ORDER_MESSAGES](state, id) {
      delete state.messenger[id];
    },
  },
  namespaced: true,
};
